import { useEffect, useState, useRef } from 'react';

export const TURN_COUNTDOWN_SEC = 10;
const TURN_COUNTDOWN_MS = TURN_COUNTDOWN_SEC * 1000;

export function useTurnCountdown({ enabled, paused = false, turnKey, onExpire }) {
  const [percent, setPercent] = useState(100);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const startRef = useRef(null);
  const pausedAccumMsRef = useRef(0);
  const pauseStartedAtRef = useRef(null);
  const didExpireRef = useRef(false);

  useEffect(() => {
    startRef.current = null;
    pausedAccumMsRef.current = 0;
    pauseStartedAtRef.current = null;
    didExpireRef.current = false;
    setPercent(100);
  }, [turnKey]);

  useEffect(() => {
    if (!enabled) {
      startRef.current = null;
      pausedAccumMsRef.current = 0;
      pauseStartedAtRef.current = null;
      didExpireRef.current = false;
      setPercent(100);
      return undefined;
    }

    if (paused) {
      if (startRef.current != null && pauseStartedAtRef.current == null) {
        pauseStartedAtRef.current = Date.now();
      }
      return undefined;
    }

    if (pauseStartedAtRef.current != null) {
      pausedAccumMsRef.current += Date.now() - pauseStartedAtRef.current;
      pauseStartedAtRef.current = null;
    }

    if (startRef.current == null) {
      startRef.current = Date.now();
      pausedAccumMsRef.current = 0;
      didExpireRef.current = false;
      setPercent(100);
    }

    const tick = () => {
      const elapsed = Date.now() - startRef.current - pausedAccumMsRef.current;
      const remaining = Math.max(0, 1 - elapsed / TURN_COUNTDOWN_MS);
      setPercent(remaining * 100);
      if (elapsed >= TURN_COUNTDOWN_MS) {
        setPercent(0);
        if (!didExpireRef.current) {
          didExpireRef.current = true;
          onExpireRef.current?.();
        }
      }
    };

    tick();
    const id = setInterval(tick, 50);

    return () => clearInterval(id);
  }, [enabled, paused, turnKey]);

  const secondsLeft = Math.max(0, Math.ceil((percent / 100) * TURN_COUNTDOWN_SEC));
  return { percent, secondsLeft };
}
